import { agentCounts, agentsApi } from '../agents-api.ts';
import type { AgentInfo, ProjectInfo } from '../agents-api.ts';
import { badge, button, emptyState, field, openDialog, pageHeader, toast } from '../components.ts';
import { icon } from '../icons.ts';
import { h } from '../ui.ts';
import { statusTone } from './agents/parts.ts';

/** Projects she builds (design-docs/0030): her own, with agents on branches, or linked ones Atlas builds via Linear. */
export async function projectsView(): Promise<HTMLElement> {
  const [projects, agents] = await Promise.all([agentsApi.projects(), agentsApi.list().catch(() => [])]);
  return h(
    'div',
    { class: 'page' },
    pageHeader(
      'Projects',
      'Each project is a repository. Talk about one, or start agents on it.',
      button([icon.plus(), 'New project'], () => newProjectDialog(), { extra: 'glow' }),
    ),
    projects.length
      ? h(
          'div',
          { class: 'cards' },
          ...projects.map((p) =>
            projectCard(
              p,
              agents.filter((a) => a.project === p.slug),
            ),
          ),
        )
      : emptyState(
          'No projects yet',
          'A new project gets a private GitHub repository, or bring an existing one.',
          button([icon.plus(), 'New project'], () => newProjectDialog(), { variant: 'outline' }),
        ),
  );
}

export const linkedBadge = () => badge('accent', icon.link(), 'Linear · builds via Atlas');

function projectCard(p: ProjectInfo, agents: AgentInfo[]): HTMLElement {
  const c = agentCounts(agents);
  return h(
    'a',
    { class: 'card project-card', href: `#/projects/${encodeURIComponent(p.slug)}` },
    h(
      'div',
      { class: 'card-head' },
      h('span', { class: 'card-title row' }, h('span', { class: `dot tone-${statusTone(agents)}` }), p.name),
      p.linearOrg ? linkedBadge() : null,
    ),
    h('p', { class: 'muted clamp' }, p.description || 'No description.'),
    h(
      'div',
      { class: 'row mono muted' },
      p.repository ? h('span', { class: 'row' }, icon.branch(12), p.repository) : h('span', {}, 'server-only repo'),
      h('span', { class: 'grow' }),
      agents.length ? h('span', {}, `${c.running} running · ${agents.length} agents`) : null,
      c.asking ? badge('warning', `${c.asking} asking`) : null,
    ),
  );
}

/** "New project": POST /projects. Without a repository the daemon creates a private GitHub repository. */
export function newProjectDialog() {
  const name = h('input', { class: 'input', name: 'name', required: true, placeholder: 'My new app', maxlength: '80' });
  const description = h('textarea', {
    class: 'textarea',
    name: 'description',
    rows: '3',
    placeholder: 'A landing page and waitlist for the Convasi app.',
  });
  const repo = h('input', {
    class: 'input mono',
    name: 'repository',
    placeholder: 'owner/name',
    pattern: '[A-Za-z0-9_.-]+/[A-Za-z0-9_.-]+',
  });
  openDialog(
    'New project',
    [
      field('Name', name),
      field('Description', description, 'What it is; agents read this.'),
      field('Existing repository', repo, 'Optional. Leave empty to create a new private GitHub repository.'),
    ],
    {
      label: 'Create project',
      run: async () => {
        const p = await agentsApi.createProject({
          name: name.value.trim(),
          ...(description.value.trim() ? { description: description.value.trim() } : {}),
          ...(repo.value.trim() ? { repository: repo.value.trim() } : {}),
        });
        toast(`Created ${p.name}`);
        location.hash = `#/projects/${encodeURIComponent(p.slug)}`;
        return true;
      },
    },
  );
  setTimeout(() => name.focus(), 0);
}
